import React, { useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { DialogDescription, DialogTitle } from 'components/ui/dialog';
import { ExamDialog } from './ExamDialog';

export interface SubmitDialogProps {
    open: boolean;
    /** Close without submitting (cancel button, Escape, scrim). */
    onCancel: () => void;
    onConfirm: () => void;
    isPreview: boolean;
    isSubmitting: boolean;
    answeredCount: number;
    totalQuestions: number;
    flaggedCount: number;
    /** 1-based numbers of the questions still without an answer, in order. */
    unansweredNumbers: number[];
    /** Where a chip jumps to; defaults to question number - 1 (one number per question). */
    targetOf?: (number: number) => { index: number; subQuestionId?: string };
    /** False for questions forced-linear navigation keeps closed (0-based index). */
    canGoToQuestion: (index: number) => boolean;
    /** Jump to a question (0-based index); the parent also closes the dialog. */
    onGoToQuestion: (index: number, subQuestionId?: string) => void;
}

/** "Testni yakunlash" confirmation: counts, a chip per unanswered question, cancel + lime submit. */
export function SubmitDialog({
    open,
    onCancel,
    onConfirm,
    isPreview,
    isSubmitting,
    answeredCount,
    totalQuestions,
    flaggedCount,
    unansweredNumbers,
    targetOf = (number) => ({ index: number - 1 }),
    canGoToQuestion,
    onGoToQuestion,
}: SubmitDialogProps) {
    const { t } = useTranslation();
    // Submitting can't be undone, so focus starts on the safe choice.
    const cancelRef = useRef<HTMLButtonElement>(null);
    const unansweredCount = unansweredNumbers.length;

    return (
        <ExamDialog open={open} onClose={onCancel} widthClass="max-w-[480px]" initialFocusRef={cancelRef}>
            <DialogTitle className="text-lg font-semibold leading-tight tracking-normal">
                {isPreview ? t('end_preview') : t('submit_assessment')}
            </DialogTitle>
            <DialogDescription className="mt-2 text-[14.5px] leading-normal">
                {isPreview
                    ? t('no_results_will_be_saved_this_is_only_a_preview_of_the_assessment')
                    : t('once_submitted_you_cannot_change_your_answers')}
            </DialogDescription>

            <dl className="mt-[18px] divide-y divide-border rounded-md border border-border text-sm">
                <div className="flex items-center justify-between gap-4 px-3.5 py-3">
                    <dt className="text-foreground/80">{t('exam_questions_answered')}</dt>
                    <dd className="font-semibold tabular-nums">
                        {answeredCount} / {totalQuestions}
                    </dd>
                </div>
                <div className="flex items-center justify-between gap-4 px-3.5 py-3">
                    <dt className="text-foreground/80">{t('exam_questions_flagged')}</dt>
                    <dd className="font-semibold tabular-nums">{flaggedCount}</dd>
                </div>
            </dl>

            {unansweredCount > 0 && (
                <>
                    <p className="mt-4 text-sm font-semibold text-warning-700 dark:text-warning-400">
                        {t('exam_unanswered_count', { count: unansweredCount })}
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {unansweredNumbers.map((number) => {
                            const target = targetOf(number);
                            return (
                                <button
                                    key={number}
                                    type="button"
                                    aria-label={t('exam_go_to_question', { number })}
                                    disabled={!canGoToQuestion(target.index)}
                                    onClick={() => onGoToQuestion(target.index, target.subQuestionId)}
                                    className="inline-flex h-8 min-w-8 items-center justify-center rounded-sm border border-border bg-card px-1.5 font-mono text-[12.5px] tabular-nums text-foreground/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 enabled:hover:border-foreground/30 enabled:hover:bg-background disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {number}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            <div className="mt-6 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
                <Button ref={cancelRef} type="button" variant="outline" size="lg" onClick={onCancel}>
                    {t('review_answers')}
                </Button>
                <Button type="button" size="lg" className="gap-2" onClick={onConfirm} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            {t('submitting')}
                        </>
                    ) : (
                        t('submit_now')
                    )}
                </Button>
            </div>
        </ExamDialog>
    );
}

export default SubmitDialog;
